import { ImmomioSpinner } from '@immomio/ui/spinner';
import {
  ImmomioButtonComponent,
  ImmomioIconButtonComponent
} from '@immomio/ui/button';
import { ImmomioPagination } from '@immomio/ui/pagination';
import {
  Component,
  inject,
  input,
  model,
  OnInit,
  viewChild,
  DestroyRef,
  ChangeDetectionStrategy
} from '@angular/core';
import {
  ImmomioAccordionComponent,
  ImmomioAccordionContentDirective,
  ImmomioAccordionHeaderDirective,
  ImmomioAccordionItemDirective
} from '@immomio/ui/accordion';
import { Store } from '@ngrx/store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { map } from 'rxjs/operators';
import { combineLatest, Observable } from 'rxjs';

import { Attachment, Pagination } from '@ui/shared/model';
import { AsyncPipe } from '@angular/common';

import { LoadingSpinnerComponent } from '../../../loading-spinner/loading-spinner.component';
import { MessageTemplateFormComponent } from '../message-template-form/message-template-form.component';
import {
  MessageTemplate,
  MessageTemplateSubstitution
} from '../../model/interface';
import { ActionState } from '../../../../../state-utils/action-state/action-state.model';
import {
  CreateTemplate,
  DeleteTemplate,
  LoadTemplate,
  UpdateTemplate
} from '../../../../../infrastructure/base-state/messenger/messenger.actions';
import {
  getCreateTemplateActionState,
  getDeleteTemplateActionState,
  getMessageTemplates,
  getMessageTemplatesActionState,
  getMessageTemplatesPage,
  getUpdateTemplateActionState
} from '../../../../../infrastructure/base-state/messenger/messenger.selector';

@Component({
  selector: 'app-message-templates',
  templateUrl: './message-templates.component.html',
  styleUrls: ['./message-templates.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ImmomioSpinner,
    ImmomioButtonComponent,
    ImmomioIconButtonComponent,
    ImmomioPagination,
    MessageTemplateFormComponent,
    ImmomioAccordionComponent,
    ImmomioAccordionItemDirective,
    ImmomioAccordionHeaderDirective,
    ImmomioAccordionContentDirective,
    LoadingSpinnerComponent,
    AsyncPipe,
    TranslatePipe
  ]
})
export class MessageTemplatesComponent implements OnInit {
  #destroyRef = inject(DestroyRef);
  private store = inject(Store);
  private translateService = inject(TranslateService);

  public templates: MessageTemplate[] = [];
  public activeTemplate: MessageTemplate;
  public showCreateTemplate = false;
  public loadedTemplatesActionState$: Observable<ActionState>;
  public createActionState$: Observable<ActionState>;
  public updateActionState$: Observable<ActionState>;
  public deleteActionState$: Observable<ActionState>;
  public loadedTemplatePagination: Pagination;
  readonly conversationMessageTemplateSubstitutionTags =
    input<MessageTemplateSubstitution[]>(undefined);
  readonly newTemplateData = model<MessageTemplate>(undefined);
  readonly accordion = viewChild(ImmomioAccordionComponent);

  public get processingAction$() {
    return combineLatest([
      this.createActionState$,
      this.updateActionState$,
      this.deleteActionState$
    ]).pipe(map(array => array.some(pending => pending.pending)));
  }

  public ngOnInit() {
    this.loadedTemplatesActionState$ = this.store.select(
      getMessageTemplatesActionState
    );
    this.store
      .select(getMessageTemplatesPage)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(pagination => (this.loadedTemplatePagination = pagination));
    this.createActionState$ = this.store.select(getCreateTemplateActionState);
    this.updateActionState$ = this.store.select(getUpdateTemplateActionState);
    this.deleteActionState$ = this.store.select(getDeleteTemplateActionState);
    this.store.dispatch(new LoadTemplate({ page: 0 }));

    this.store
      .select(getMessageTemplates)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(templates => {
        this.templates = templates;
        const newTemplateData = this.newTemplateData();
        if (newTemplateData) {
          this.activeTemplate = { ...newTemplateData };
        }
      });
  }

  public saveTemplate(template: MessageTemplate) {
    const { attachments, ...payload } = template;
    if ((attachments as Attachment)?.file) {
      (payload as MessageTemplate).attachments = (
        attachments as Attachment
      ).file;
    } else if (attachments) {
      (payload as MessageTemplate).attachments = Array.isArray(attachments)
        ? attachments[0]
        : attachments;
    }
    this.newTemplateData.set(null);
    this.accordion()?.collapseAll();
    if (!template.id) {
      this.cancelCreate();
      return this.store.dispatch(new CreateTemplate(payload));
    }
    return this.store.dispatch(new UpdateTemplate(payload));
  }

  public panelChanged(opened: boolean, index: number) {
    if (!opened) return;
    this.cancelCreate();
    this.activeTemplate = this.templates[index];
  }

  public createTemplate() {
    this.accordion()?.collapseAll();
    this.showCreateTemplate = true;
    this.activeTemplate = {
      id: null,
      title: this.translateService.instant('messenger.new_template_title_l'),
      content: '',
      attachments: null
    };
  }

  public deleteTemplate(event: MouseEvent, templateId: string, index: number) {
    if (!templateId) return this.templates.splice(index, 1);
    this.store.dispatch(new DeleteTemplate(templateId));
    this.accordion()?.collapseAll();
    event.preventDefault();
    event.stopPropagation();
  }

  public onPageChange(page: number) {
    this.store.dispatch(
      new LoadTemplate({
        page,
        size: this.loadedTemplatePagination.size
      })
    );
  }

  public onSizeChange(size: number) {
    this.store.dispatch(new LoadTemplate({ page: 0, size }));
  }

  public cancelCreate() {
    this.showCreateTemplate = false;
  }
}
